import { Injectable } from '@angular/core';

export interface ErrorMessages {
    heading: string;
    message: string;
}

export interface BackendError {
    Id: string;
    operationId?: string;
}

type ErrorEntry = { header: string; body: string };

@Injectable({ providedIn: 'root' })
export class ErrorService {

    private readonly GENERIC_ERROR: ErrorEntry = {
        header: 'web.errorCode1000GenericErrorHeader',
        body: 'web.errorCode1000GenericErrorBody'
    };

    private readonly CAPTCHA_ERROR: ErrorEntry = {
        header: 'web.errorCode1001GenericReCaptchaErrorHeader',
        body: 'web.errorCode1001GenericReCaptchaErrorBody'
    };

    private readonly PAYMENT_ERROR: ErrorEntry = {
        header: 'web.errorCode2002FailedInitializingOrderHeader',
        body: 'web.errorCode2002FailedInitializingOrderBody'
    };

    private readonly UNAUTHORIZED_ERROR: ErrorEntry = {
      header: 'web.errorCode3000UnauthorizedErrorHeader',
      body: 'web.errorCode3000UnauthorizedErrorBody'
    };

    private readonly MISSING_ERROR: ErrorEntry = {
      header: 'web.errorCode3002MissingTokenErrorHeader',
      body: 'web.errorCode3002MissingTokenErrorBody'
    };
    private readonly PURCHASE_CAP_EXCEEDED_BY_ORDER: ErrorEntry = {
      header: 'web.errorCodePurchaseCapReachedHeader',
      body: 'web.errorCodePurchaseCapReachedBody'
    };

    private readonly EXPIRED_TICKET_ERROR: ErrorEntry = {
      header: 'web.errorCodeExpiredTicketOnCreationHeader',
      body: 'web.errorCodeExpiredTicketOnCreationBody'
    };

    private readonly errorCodeMap: Record<string, ErrorEntry> = {
        // Generic errors
        'REJSEBILLET.WEB.GENERIC_ERROR': this.GENERIC_ERROR,

        // Failed to load purchase page
        'REJSEBILLET.WEB.PURCHASE.LOADING_FAILED': {
            header: 'web.errorCode2001FailedLoadingHeader',
            body: 'web.errorCode2001FailedLoadingBody'
        },

        // ReCaptcha errors
        'REJSEBILLET.CAPTCHA.INVALID': this.CAPTCHA_ERROR,
        'REJSEBILLET.CAPTCHA.INVALID.ACTION': this.CAPTCHA_ERROR,
        'REJSEBILLET.CAPTCHA.ABUSIVE': this.CAPTCHA_ERROR,
        'REJSEBILLET.CAPTCHA.SMS_RISKY': this.CAPTCHA_ERROR,

        // POST order errors
        'REJSEBILLET.WEB.PURCHASE.ORDER_CREATION_FAILED': this.PAYMENT_ERROR,
        'REJSEBILLET.WEB.PURCHASE.PAYMENT_FAILED': this.PAYMENT_ERROR,
        'REJSEBILLET.WEB.PURCHASE.PAYMENT_CANCELLED': this.PAYMENT_ERROR,
        'REJSEBILLET.VALIDATION.TICKET.EXPIRED_ON_CREATION': this.EXPIRED_TICKET_ERROR,
        'REJSEBILLET.VALIDATION.SPECIAL_TICKET.PURCHASE_CAP_EXCEEDED_BY_ORDER': this.PURCHASE_CAP_EXCEEDED_BY_ORDER,

        // GET /order errors
        'REJSEBILLET.VALIDATION.WEB.MISSING_AUTHENTICATION_TOKEN': this.MISSING_ERROR,
        'REJSEBILLET.VALIDATION.WEB.INVALID_AUTHENTICATION_TOKEN': this.UNAUTHORIZED_ERROR,
        'REJSEBILLET.WEB.TICKET_INSPECTION.LOADING_FAILED': {
            header: 'web.errorCode3001FailedLoadingHeader',
            body: 'web.errorCode3001FailedLoadingBody'
        }
    };

    getErrorMessages(errorCode: string): ErrorMessages {
        const keys = this.errorCodeMap[errorCode] ?? this.GENERIC_ERROR;

        return {
            heading: keys.header,
            message: keys.body
        };
  }

  hasErrorCode(errorCode: string): boolean {
    return errorCode in this.errorCodeMap;
  }
}
